import { useParams, useLocation, Link, Navigate } from 'react-router-dom';
import useFetch, { Status } from '../../../../../hooks/fetch.hook';
import Spinner from '../../../../Shared/Spinner';
import LegStepIndicator from '../../../Shared/LegStepIndicator';
import InformationBox from '../../../Shared/InformationBox';
import PageIntroduction from '../../../Shared/PageIntroduction';
import Breadcumb from '../../../Shared/Breadcrumb';
import penIcon from '../../../../../../assets/icons/pen.svg';
import DataUrlHelper from '../../../../../helpers/data-url.helper';
import { ConfigurationService } from '../../../../../services/configuration.service';
import { TranslationService } from '../../../../../services/translation.service';
import LinkToParticipationOverviewPuff from '../../../Shared/LinkToParticipationOverviewPuff';
import ParticipationResultApiModel from '../../../models/participation-result.api-model';
import type { IParticipationResult } from '../../../../../models/pa-egna-ben/participation-result';
import { Role } from '../../../../../models/pa-egna-ben/role.enum';
import NotFoundRedirect from '../../../Shared/NotFoundRedirect';
import { AnalyticsService } from '../../../../../services/analytics-service';
import { InformationBoxType } from '../../../Shared/InformationBox/information-box-type.enum';

function processRecievedData(data: ParticipationResultApiModel): IParticipationResult {
  return ParticipationResultApiModel.mapToBusinessModel(data);
}

function Participation() {
  // === HOOKS ===
  const { participationId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // === STATE ===
  const [getParticipation] = useFetch<ParticipationResultApiModel>(`/api/paegnaben/teacher/participations/${participationId}/result`);

  // === VARIABLES ===
  const participationResult = getParticipation.data ? processRecievedData(getParticipation.data) : undefined;
  const faqLink = ConfigurationService.get('paegnaben.teacherpage.faqlink');
  const faqLinkText = TranslationService.get('paegnaben.teacherpage.faqlinktext');

  // === RENDER ===
  if (getParticipation.status === Status.failed && getParticipation.httpResponseStatusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (getParticipation.status === Status.failed && getParticipation.httpResponseStatusCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }
  if (getParticipation.status === Status.failed && getParticipation.httpResponseStatusCode === 404) {
    return <NotFoundRedirect />;
  }

  return (
    <div>
      {getParticipation.status === Status.loading && <Spinner />}
      {getParticipation.status === Status.success && participationResult && (
        <div>
          <Breadcumb
            items={[
              { text: 'Mina sidor', url: '/' },
              { text: `${participationResult.participationName}`, url: undefined },
            ]}
          />
          <PageIntroduction heading={participationResult?.participationName} />

          {queryParams.get('a') === 'poang-registrerade' && (
            <InformationBox type={InformationBoxType.Success} headingElement={<span className="fw-bold">Poäng har rapporterats</span>} className="mb-4 mb-md-5" setFocus={true} />
          )}

          {queryParams.get('a') === 'klass-sparad' && (
            <InformationBox type={InformationBoxType.Success} headingElement={<span className="fw-bold">Ändringar har sparats</span>} className="mb-4 mb-md-5" setFocus={true} />
          )}

          <LegStepIndicator participationId={Number(participationId)} competitionLegs={participationResult.legs} legResults={participationResult.legResults} viewerRole={Role.teacher} />

          <Link
            to={`/klasser/${participationId}/klassuppgifter`}
            className="d-flex align-items-center mt-4 mt-md-5"
            onClick={() => AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Klassida, klick på `Klassuppgifter`')}
          >
            <img src={DataUrlHelper.encode(penIcon)} alt="" className="me-1" /> Klassuppgifter
          </Link>

          {faqLink && faqLinkText && (
            <a href={faqLink} className="d-flex mt-4 mt-md-5">
              {faqLinkText}
            </a>
          )}

          <LinkToParticipationOverviewPuff participationId={Number(participationId)} className="mt-4 mt-md-5" />
        </div>
      )}
    </div>
  );
}

export default Participation;
